import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { api, till } from "@/lib/api";
import { ago, moneyShort, personName } from "@/lib/format";
import { useShop, useTill } from "@/lib/till-context";
import { ROLE_LABEL, type StaffLite, type StaffRole } from "@/lib/types";
import { Avatar, Badge, Button, Field, Input, Modal, Pager, Select, cx, usePaged, useToast, PasswordInput } from "@/components/ui";
import { Card, PageHead, td, th } from "@/components/Layout";
import { StaffPerformance } from "@/components/Insights";
import { LEVEL_SUB, roleName, useTillRoles } from "@/components/TillRoleBits";

const COLORS = ["#f59e0b", "#99f6e4", "#fda4af", "#bfdbfe", "#d9f99d", "#e9d5ff", "#fed7aa"];

export function Staff() {
  const qc = useQueryClient();
  const { refetchMe } = useTill();
  const q = useQuery({ queryKey: ["staff"], queryFn: () => api<{ staff: StaffLite[] }>(till("/admin/staff")) });
  const shopsQ = useQuery({ queryKey: ["shops"], queryFn: () => api<{ shops: { id: string; name: string }[] }>(till("/admin/shops")) });
  const shopList = shopsQ.data?.shops ?? [];
  const [find, setFind] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const tillRoles = useTillRoles().data?.roles;
  const [shopFilter, setShopFilter] = useState("");
  const everyone = q.data?.staff ?? [];
  const shown = everyone.filter((p) => (!roleFilter || (roleFilter.startsWith("trole-") ? p.customRole === roleFilter : p.role === roleFilter && !p.customRole)) && (!shopFilter || !p.shopIds?.length || p.shopIds.includes(shopFilter)) && (!find.trim() || [personName(p.name), p.email].some((v) => v?.toLowerCase().includes(find.trim().toLowerCase()))));
  const staffPage = usePaged(shown);
  const noPin = everyone.filter((p) => p.active && !p.hasPin).length;
  const toast = useToast();
  const release = async (p: StaffLite) => {
    try {
      await api(till(`/admin/staff/${p.id}/release`), { body: {} });
      toast(`${personName(p.name)} can now log in at another till`);
      void qc.invalidateQueries({ queryKey: ["staff"] });
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't release them", "err");
    }
  };
  const [edit, setEdit] = useState<StaffLite | "new" | null>(null);
  return (
    <>
      <PageHead title="Staff & roles" sub="People who sell at the tills. They sign in with a 4-digit PIN.">
        <Button tone="primary" onClick={() => setEdit("new")}>
          Add staff
        </Button>
      </PageHead>
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          {([["Active staff", String(everyone.filter((p) => p.active).length), "Can sign in"], [import.meta.env.VITE_BRAND ? "Till managers" : "Managers", String(everyone.filter((p) => p.active && p.role === "manager").length), "Full till access"], ["Supervisors", String(everyone.filter((p) => p.active && p.role === "supervisor").length), "Approve at the till"], ["No PIN yet", String(noPin), noPin ? "Can't sign in until set" : "Everyone has one"]] as const).map(([l, v, n]) => (
            <div key={l} className="min-w-0 border border-slate-200 bg-white p-4"><span className="text-xs font-semibold text-slate-500">{l}</span><div className="mt-1 text-[24px] font-extrabold leading-tight tracking-tight">{v}</div><div className="mt-0.5 text-xs text-slate-500">{n}</div></div>
          ))}
        </div>
        <Card
          title="Staff"
          right={
            <div className="flex flex-[1_1_420px] flex-wrap justify-end gap-2">
              <Input aria-label="Search staff" value={find} onChange={(e) => setFind(e.target.value)} placeholder="Search name or email" className="!h-10 min-w-0 flex-[1_1_170px] sm:max-w-[230px]" />
              <Select aria-label="Role" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="!h-10 !w-auto">
                <option value="">All roles</option>
                {(Object.keys(ROLE_LABEL) as StaffRole[]).map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                {(tillRoles ?? []).map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
              </Select>
              {shopList.length ? <Select aria-label="Shop" value={shopFilter} onChange={(e) => setShopFilter(e.target.value)} className="!h-10 !w-auto"><option value="">All shops</option>{shopList.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</Select> : null}
            </div>
          }
        >
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  {["Name", "Shops", "Role", "PIN", "Last on", "Sales this week", ""].map((h, i) => (
                    <th key={i} className={cx(th, i >= 5 && "text-right")}>{h || <span className="sr-only">Actions</span>}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {staffPage.rows.map((p) => {
                  const mail = p.email || (p.name.includes("@") ? p.name : "");
                  return (
                    <tr key={p.id} className={cx(!p.active && "opacity-55")}>
                      <td className={td}>
                        <span className="flex items-center gap-2.5">
                          <Avatar name={personName(p.name)} color={p.color} size={34} />
                          <span className="min-w-0"><b className="flex items-center gap-1.5 font-semibold">{personName(p.name)}{!p.active ? <Badge>Can't sign in</Badge> : null}</b><span className="block max-w-[240px] truncate text-xs text-slate-500">{mail || "No email yet"}</span></span>
                        </span>
                      </td>
                      <td className={cx(td, "max-w-[200px] whitespace-normal text-[13px]")}>{p.shopIds?.length ? shopList.filter((x) => p.shopIds!.includes(x.id)).map((x) => x.name).join(", ") : <span className="text-slate-500">All shops</span>}</td>
                      <td className={td}><Badge tone={p.role === "manager" ? "dark" : p.role === "supervisor" ? "brand" : "slate"}>{roleName(p, tillRoles)}</Badge></td>
                      <td className={td}>{p.hasPin ? <span className="text-[13px] text-slate-500">Set</span> : <Badge tone="amber">Not set</Badge>}</td>
                      <td className={cx(td, "text-[13px] text-slate-500")}>{p.activeTill ? <span className="flex items-center gap-2"><Badge tone="brand">At {p.activeTill}</Badge><button type="button" className="text-[13px] font-semibold text-brand hover:underline" onClick={() => void release(p)}>Release</button></span> : p.lastSeenAt ? ago(p.lastSeenAt) : "Never"}</td>
                      <td className={cx(td, "text-right font-bold")}>{moneyShort(p.week ?? 0)}</td>
                      <td className={cx(td, "text-right")}><span className="flex items-center justify-end gap-3">{import.meta.env.VITE_BRAND ? <Link to={`/reports?teller=${encodeURIComponent(p.id)}`} aria-label={`See performance for ${personName(p.name)}`} className="text-[13px] font-semibold text-brand hover:underline">See performance</Link> : null}<Button size="sm" onClick={() => setEdit(p)} aria-label={`Edit ${personName(p.name)}`}>Edit</Button></span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {q.data && !shown.length ? <p className="px-5 py-8 text-center text-sm text-slate-500">{everyone.length ? "Nobody matches." : "No staff yet. Add your first person."}</p> : null}
          <Pager page={staffPage.page} size={staffPage.size} total={staffPage.total} onPage={staffPage.setPage} />
        </Card>
        {import.meta.env.VITE_BRAND ? null : <StaffPerformance />}
        {import.meta.env.VITE_BRAND ? null : <TillRoles />}
      </div>
      {edit ? <StaffModal shops={shopList} person={edit === "new" ? null : edit} onClose={() => setEdit(null)} onSaved={() => { void qc.invalidateQueries({ queryKey: ["staff"] }); refetchMe(); }} /> : null}
    </>
  );
}

/** What each till role (PIN) can do. On Buildwise it sits on Roles & permissions. */
export function TillRoles({ open }: { open?: boolean }) {
  const { me } = useTill();
  const shop = useShop();
  const s = me?.settings;
  const fmtLimit = (v: number | null | undefined) => (v == null ? "Any" : `Up to ${moneyShort(v)}`);
  const perms: [string, string, string, string][] = [
    ["Sell & take payment", "Yes", "Yes", "Yes"],
    ["Give a discount", `Up to ${s?.discountLimit.cashier ?? 3}%`, `Up to ${s?.discountLimit.supervisor ?? 10}%`, "Any"],
    ["Change a price at the till", "No", "Yes", "Yes"],
    ...(shop.serials ? ([["Sell without scanning the IMEI", "No", "Yes, picks from stock", "Yes"]] as [string, string, string, string][]) : []),
    ["Refund", fmtLimit(s?.refundLimit.cashier), fmtLimit(s?.refundLimit.supervisor), "Any"],
    ["Pay cash out of the drawer", "No", "Yes", "Yes"],
    ["Approve a cash difference at close", "No", "Yes", "Yes"],
    ...(s?.scanning === false ? [] : ([["Link a new barcode / quick-add product", "No", "Yes", "Yes"]] as [string, string, string, string][])),
    ...(import.meta.env.VITE_BRAND
      ? ([["Sell on account (the customer owes)", "With a PIN", "Yes", "Yes"], ["Take over or hand over a shift", "With a PIN", "Yes", "Yes"], ["A sale where the customer won't give details", "No", "No", "Yes"], ["Open the back office", "No", "No", "No"]] as [string, string, string, string][])
      : ([["Receive, transfer & adjust stock", "No", "Yes", "Yes"], ["Staff, settings & tills", "No", "Reports only", "Yes"]] as [string, string, string, string][])),
  ];
  return (
    <details open={open} className="border border-slate-200 bg-white">
      <summary className="flex min-h-14 cursor-pointer items-center justify-between gap-3 px-5 py-3">
        <span><b className="block text-base">What each till role can do</b><span className="text-[13px] font-normal text-slate-500">{import.meta.env.VITE_BRAND ? "Staff who sign in at the till with a PIN. These are fixed; the limits are set in Settings." : <>Cashier, Supervisor and Manager. Back office access is set on <Link to="/permissions" className="font-semibold text-brand underline">Roles & permissions</Link>.</>}</span></span>
      </summary>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {["Permission", ROLE_LABEL.cashier, ROLE_LABEL.supervisor, ROLE_LABEL.manager].map((h) => (
                <th key={h} className={th}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {perms.map(([n, a, b, c]) => (
              <tr key={n}>
                <td className={cx(td, "font-semibold")}>{n}</td>
                <td className={td}>{a}</td>
                <td className={td}>{b}</td>
                <td className={td}>{c}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="px-5 py-3 text-[13px] text-slate-500">Over a limit, the till asks for a supervisor's or manager's PIN on the spot and records who approved it. Limits are set in Settings.</p>
    </details>
  );
}

function StaffModal({ shops, person, onClose, onSaved }: { shops: { id: string; name: string }[]; person: StaffLite | null; onClose: () => void; onSaved: () => void }) {
  const [shopIds, setShopIds] = useState<string[]>(person?.shopIds ?? []);
  const toast = useToast();
  // Someone whose name is really an email: suggest a name and move the email to its own box.
  const [name, setName] = useState(person ? personName(person.name) : "");
  const [email, setEmail] = useState(person?.email ?? (person?.name.includes("@") ? person.name : ""));
  const [role, setRole] = useState<string>(person?.customRole ?? person?.role ?? "cashier");
  const tillRoles = useTillRoles().data?.roles ?? [];
  const delivery = !!useTill().me?.settings.delivery?.enabled;
  const [pin, setPin] = useState("");
  const [color, setColor] = useState(person?.color ?? COLORS[Math.floor(Math.random() * COLORS.length)]!);
  const [active, setActive] = useState(person?.active ?? true);
  const save = async () => {
    try {
      const body: Record<string, unknown> = { name, email, shopIds, role, color, active };
      if (pin) body.pin = pin;
      await api(till(person ? `/admin/staff/${person.id}` : "/admin/staff"), { method: person ? "PUT" : "POST", body });
      toast(person ? "Saved" : `${name} added`);
      onSaved();
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't save", "err");
    }
  };
  return (
    <Modal title={person ? `Edit ${personName(person.name)}` : "Add staff"} onClose={onClose} footer={<Button tone="primary" onClick={save} disabled={!name.trim() || (!person && !email.trim()) || (!person && shops.length > 0 && !shopIds.length) || (!person && pin.length !== 4) || (pin.length > 0 && pin.length !== 4)}>Save</Button>}>
      <div className="grid gap-3">
        <Field label="User name" hint="Shown on reports and receipts">
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} autoFocus />
        </Field>
        <Field label="Email">
          <Input type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={200} />
        </Field>
        {shops.length ? (
          <div className="flex flex-col gap-1.5">
            <span className="text-[13px] font-semibold">Shops they work in</span>
            <span className="flex flex-wrap gap-x-4 gap-y-1">
              <label className="flex min-h-11 items-center gap-2 text-sm font-semibold">
                <input type="checkbox" checked={shopIds.length === shops.length} onChange={(e) => setShopIds(e.target.checked ? shops.map((x) => x.id) : [])} className="h-5 w-5 accent-[var(--brand)]" /> All shops
              </label>
              {shops.map((x) => (
                <label key={x.id} className="flex min-h-11 items-center gap-2 text-sm">
                  <input type="checkbox" checked={shopIds.includes(x.id)} onChange={(e) => setShopIds(e.target.checked ? [...shopIds, x.id] : shopIds.filter((v) => v !== x.id))} className="h-5 w-5 accent-[var(--brand)]" /> {x.name}
                </label>
              ))}
            </span>
            <span className="text-xs text-slate-500">Their PIN only works on tills in these shops.{person ? " None ticked means every shop." : ""}</span>
          </div>
        ) : null}
        {import.meta.env.VITE_BRAND ? (
          <div className="flex flex-col gap-1.5">
            <span className="text-[13px] font-semibold">Role</span>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {([["cashier", LEVEL_SUB.cashier, ROLE_LABEL.cashier], ["supervisor", LEVEL_SUB.supervisor, ROLE_LABEL.supervisor], ["manager", LEVEL_SUB.manager, ROLE_LABEL.manager], ...tillRoles.map((r) => [r.id, `Works like ${ROLE_LABEL[r.base]}`, r.label]), ...(delivery || role === "yard" ? [["yard", "Only the Yard screen: loading and sending out deliveries", ROLE_LABEL.yard]] : [])] as [string, string, string][]).map(([id, sub, name]) => (
                <button key={id} type="button" aria-pressed={role === id} onClick={() => setRole(id)} className={cx("flex flex-col items-start gap-0.5 rounded-xl border-[1.5px] px-3 py-2.5 text-left", role === id ? "border-brand bg-brand-tint" : "border-slate-200 bg-white")}>
                  <b className="text-sm">{name}</b>
                  <span className="text-xs text-slate-500">{sub}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
        <Field label="Role">
          <Select value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="cashier">Cashier: sells, small discounts and refunds</option>
            <option value="supervisor">Supervisor: approvals, stock, reports</option>
            <option value="manager">Manager: everything, staff and settings</option>
            {delivery || role === "yard" ? <option value="yard">Yard staff: only the Yard screen (loading and sending out deliveries)</option> : null}
          </Select>
        </Field>
        )}
        <Field label={person ? "New PIN (leave blank to keep)" : "4-digit PIN"} hint="Each person's PIN must be different, with no repeats or runs like 1234">
          <PasswordInput inputMode="numeric" maxLength={4} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} className="font-mono text-lg tracking-[0.4em]" />
        </Field>
        <div>
          <span className="text-[13px] font-semibold">Colour</span>
          <div className="mt-1.5 flex gap-2">
            {COLORS.map((c) => (
              <button key={c} type="button" aria-label={c} onClick={() => setColor(c)} style={{ background: c }} className={cx("h-9 w-9", color === c && "ring-2 ring-slate-900 ring-offset-2")} />
            ))}
          </div>
        </div>
        {person ? (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="h-5 w-5 accent-[var(--brand)]" /> Can sign in
          </label>
        ) : null}
      </div>
    </Modal>
  );
}
